import { useState } from 'react';
import { MOMENTO } from '@copathy/shared';
import { Contenedor } from '../componentes/Contenedor';
import { VistaFormulario } from '../componentes/VistaFormulario';
import { BotonVolver } from '../componentes/BotonVolver';
import { Enviar, Reloj } from '../componentes/iconos';
import { useMigas } from '../lib/migas';
import { useActividad, useMisActividades } from '../lib/aplicaciones';

/**
 * Mis actividades — lo que ve el TRABAJADOR al entrar.
 *
 * ── Esta pantalla es el borrador de la app móvil ──
 * La app (D-011) va a abrir exactamente en esta lista: «qué tengo pendiente».
 * Consume `/api/actividades`, la misma puerta que consumirá la app, así que
 * cada decisión de acá —qué se muestra, qué no— ya quedó tomada para las dos.
 *
 * ⚠️ **Responder todavía no existe** (es M7): al abrir una actividad se ve el
 * cuestionario completo, paginado, pero inerte. Se dice en pantalla — una
 * pantalla que parece rota es peor que una que explica qué falta.
 */

const cierreLegible = (iso: string | null): string => {
  if (!iso) return 'Sin fecha límite';
  const ms = new Date(iso).getTime() - Date.now();
  const horas = Math.round(ms / 3_600_000);
  if (horas < 24) return `Cierra en ${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  const dias = Math.round(horas / 24);
  return `Cierra en ${dias} ${dias === 1 ? 'día' : 'días'}`;
};

export function Actividades() {
  const [abierta, setAbierta] = useState<number | null>(null);
  useMigas([{ texto: 'Mis actividades' }]);

  const lista = useMisActividades();
  const detalle = useActividad(abierta ?? 0);

  if (abierta !== null) {
    return (
      <Contenedor>
        <div className="mb-5">
          {/* Vuelve a la lista, no a otra página: la actividad abierta es estado
              de esta pantalla, y el botón atrás del navegador no la conoce. */}
          <button type="button" onClick={() => setAbierta(null)} className="contents">
            <BotonVolver a="/actividades">Mis actividades</BotonVolver>
          </button>
        </div>

        {detalle.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}
        {detalle.isError && (
          <p className="py-16 text-center text-red-600">
            No pudimos abrir esta actividad. Puede que ya haya cerrado.
          </p>
        )}

        {detalle.data && (
          <>
            <p className="mx-auto mb-4 w-[min(100%,23.5rem)] rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-center text-sm text-marino-500">
              Esta es una vista de consulta: <strong>se responde desde la app móvil</strong> de
              COPATHY.
            </p>
            <VistaFormulario
              cuestionarioId={detalle.data.actividad.cuestionarioId}
              tienePortada={detalle.data.actividad.tienePortada}
              nombre={detalle.data.actividad.titulo}
              descripcion={MOMENTO[detalle.data.actividad.momento]}
              preguntas={detalle.data.version.preguntas}
              escalas={detalle.data.escalas}
              idResaltado={null}
            />
          </>
        )}
      </Contenedor>
    );
  }

  return (
    <Contenedor>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-marino-800">Mis actividades</h1>
        <p className="mt-1 text-sm text-marino-400">
          Los cuestionarios que tienes pendientes. Nadie de tu empresa ve tus respuestas
          individuales: eso es entre tú y COPATHY.
        </p>
      </div>

      {lista.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}
      {lista.isError && (
        <p className="py-16 text-center text-red-600">
          No pudimos cargar tus actividades. Recarga la página.
        </p>
      )}

      {lista.data?.length === 0 && (
        <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
            <Enviar size={24} />
          </div>
          <h2 className="font-semibold text-marino-700">Nada pendiente</h2>
          <p className="mt-1 text-sm text-marino-400">
            Cuando COPATHY te asigne un cuestionario, aparece acá.
          </p>
        </div>
      )}

      <ul className="grid gap-3 md:grid-cols-2">
        {lista.data?.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => setAbierta(a.id)}
              className="group w-full rounded-2xl border border-marino-100 bg-white p-5 text-left
                transition hover:border-marino-300 hover:shadow-sm
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
            >
              <h2 className="font-semibold text-marino-800">{a.titulo}</h2>
              <p className="mt-1 text-sm text-marino-400">
                {MOMENTO[a.momento]} · {a.preguntas} {a.preguntas === 1 ? 'pregunta' : 'preguntas'}
              </p>
              <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-naranja-600">
                <Reloj size={14} />
                {cierreLegible(a.cierraEn)}
              </p>
            </button>
          </li>
        ))}
      </ul>
    </Contenedor>
  );
}
